import { useState } from "react";
import {
  createStock,
  deleteStockById,
  editStockById,
  getStocks,
} from "../../../Services/inventoryService";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "../constants";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

export const useStock = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: null,
    product: null,
    warehouse: null,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const onPageChange = (newPage) => setPage(newPage);

  const onPageSizeChange = (newSize) => {
    setRowsPerPage(newSize);
    setPage(1);
  };

  const fetchStocks = async (params) => {
    setLoading(true);
    var data = null;
    try {
      const response = await getStocks({
        ...params,
        ...filters,
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
      });
      if (response.status === 200) {
        setTotalPages(Math.max(1, Math.ceil((response.data?.count || 0) / rowsPerPage)));
        data = response.data?.results;
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.SITES_FETCH_FAILED;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_FETCH_STOCK
      );
    } finally {
      setLoading(false);
      return data;
    }
  };

  const createStockData = async (payload) => {
    setLoading(true);
    var data = null;
    try {
      const response = await createStock(payload);
      if (response.status === 201) {
        data = response.data;
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.SITES_FETCH_FAILED;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_CREATE_STOCK
      );
    } finally {
      setLoading(false);
      return data;
    }
  };

  const patchStockData = async (id, payload) => {
    setLoading(true);
    var data = null;
    try {
      const response = await editStockById(payload, id);
      if (response.status === 200) {
        toast.success(TOAST_MESSAGES.SUCCESS_UPDATE_STOCK);
        data = response.data;
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.FAILED_UPDATE_STOCK;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_UPDATE_STOCK
      );
    } finally {
      setLoading(false);
      return data;
    }
  };

  const deleteStockData = async (id) => {
    setLoading(true);
    var data = null;
    try {
      const response = await deleteStockById(id);
      if (response.status === 204) {
        toast.success(TOAST_MESSAGES.SUCCESS_DELETE_STOCK);
        data = true;
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.FAILED_DELETE_STOCK;
        toast.error(msg);
        data = false;
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_DELETE_STOCK
      );
      data = false;
    } finally {
      setLoading(false);
      return data;
    }
  };

  const fetchWarehouseProducts = async ({ warehouseId, categoryId, make, search = "", } = {}) => {
    if (!warehouseId) return [];
    try {
      const params = {
        warehouse: warehouseId,
        search,
        limit: 10,
        offset: 0,
      };

      if (categoryId) params["product__category"] = categoryId;
      if (make) params["product__make"] = make;

      const response = await getStocks(params);

      if (response.status === 200 && response.data?.results?.length) {
        return response.data.results.map((item) => ({
          id: item.product.id,
          product_name: item.product.product_name,
          specification: item.product.specification,
          UOM: item.product.UOM_name,
          available_quantity: item.available_quantity,
        }));
      }
      return [];
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.ERROR_FETCH_PRODUCTS
      );
      return [];
    }
  };

  return {
    loading,
    filters,
    setFilters,
    page,
    totalPages,
    rowsPerPage,
    onPageChange,
    onPageSizeChange,
    fetchStocks,
    createStockData,
    patchStockData,
    deleteStockData,
    fetchWarehouseProducts,
  };
};
